import React from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription
} from '../ui/dialog';

/**
 * Le modal du module inter-sites, sur les primitives Dialog.
 *
 * Il en existait TROIS copies divergentes — une par fichier — toutes bâties sur
 * un overlay maison en `createPortal`. Le modèle visuel l'interdit, et pas pour
 * une raison de goût : Radix pose `pointer-events: none` sur le `body` tant
 * qu'un Dialog est ouvert. Un overlay maison rendu hors de son portail
 * s'affiche normalement mais ne reçoit AUCUN clic. Le jour où un modal
 * inter-sites s'ouvre au-dessus d'un Dialog, il devient inerte.
 *
 * Passer par Radix apporte en prime ce que les copies n'avaient pas : fermeture
 * à la touche Échap, piège à focus, retour du focus à l'élément d'origine,
 * et empilement correct des modals imbriqués.
 *
 * Les largeurs suivent la table du modèle visuel. Le format est large plutôt
 * que long : un modal qui déborde se réorganise en colonnes avant de gagner un
 * ascenseur.
 */
const LARGEURS = {
  // Liste ou historique
  liste: 'w-[95vw] max-w-4xl',
  // Formulaire à deux colonnes
  formulaire: 'w-[95vw] max-w-5xl',
  // Parcours en étapes
  etapes: 'w-[95vw] max-w-6xl',
  // Dossier complet, tableaux
  dossier: 'w-[95vw] max-w-7xl',
  // Confirmation ou saisie courte : le seul cas où l'étroitesse se justifie
  action: 'w-[95vw] max-w-lg'
};

const ModalWrapper = ({
  children,
  onClose,
  title,
  subtitle,
  taille = 'action',
  icone: Icone = null,
  pied = null,
  // Conservé pour les appels existants : `wide` valait max-w-2xl.
  wide = false
}) => {
  const largeur = LARGEURS[taille] || (wide ? LARGEURS.liste : LARGEURS.action);

  return (
    <Dialog open onOpenChange={(ouvert) => { if (!ouvert) onClose?.(); }}>
      <DialogContent
        className={`${largeur} !max-h-[92vh] p-0 gap-0 overflow-hidden flex flex-col`}
      >
        {/* En-tête au format de la charte : pastille d'icône, titre, sous-titre. */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            {Icone && (
              <span className="w-10 h-10 rounded-lg bg-[#246990] text-white flex items-center justify-center flex-shrink-0">
                <Icone size={18} />
              </span>
            )}
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900 truncate">
                {title}
              </DialogTitle>
              {subtitle && (
                <DialogDescription className="text-sm text-gray-500 mt-0.5 truncate">
                  {subtitle}
                </DialogDescription>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {children}
        </div>

        {pied && (
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
            {pied}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalWrapper;
export { LARGEURS };
